import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useMap } from "react-leaflet";
import { CALM_MS, windAt } from "./wind";

// Screen distance between arrows; alternate rows are offset by half
const SPACING = 76;

const arrowLength = (speed) => 10 + Math.min(speed, 15) * 1.6;

// A light field of arrows showing which way the air is moving and how fast,
// estimated from nearby weather stations. Drawn in screen space inside a
// Leaflet pane so it pans with the map, and redrawn when the view settles.
export const WindField = ({ weather, pane }) => {
  const map = useMap();
  const [frame, setFrame] = useState(null);

  useEffect(() => {
    const update = () => {
      const size = map.getSize();
      const arrows = [];
      for (let row = 0, y = SPACING / 2; y < size.y; row++, y += SPACING) {
        const offset = row % 2 ? SPACING / 2 : 0;
        for (let x = SPACING / 2 + offset; x < size.x; x += SPACING) {
          const { lat, lng } = map.containerPointToLatLng([x, y]);
          const wind = windAt(lat, lng, weather);
          if (wind) arrows.push({ x, y, ...wind });
        }
      }
      setFrame({
        origin: map.containerPointToLayerPoint([0, 0]),
        size,
        arrows,
      });
    };
    // Positions are stale while zooming, so hide until the zoom ends
    const hide = () => setFrame(null);

    update();
    map.on("moveend resize", update);
    map.on("zoomstart", hide);
    return () => {
      map.off("moveend resize", update);
      map.off("zoomstart", hide);
    };
  }, [map, weather]);

  const target = map.getPane(pane);
  if (!frame || !target) return null;

  return createPortal(
    <svg
      className="aq-wind"
      width={frame.size.x}
      height={frame.size.y}
      style={{ left: frame.origin.x, top: frame.origin.y }}
      aria-hidden="true"
    >
      {frame.arrows.map(({ x, y, speed, towards }) => {
        if (speed < CALM_MS) {
          return <circle key={`${x}-${y}`} cx={x} cy={y} r={2} />;
        }
        const half = arrowLength(speed) / 2;
        return (
          <path
            key={`${x}-${y}`}
            transform={`translate(${x} ${y}) rotate(${towards})`}
            d={`M0 ${half} V${-half} M-3.5 ${-half + 5} L0 ${-half} L3.5 ${-half + 5}`}
          />
        );
      })}
    </svg>,
    target,
  );
};
